import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { storageService } from '@/services/local-storage';
import type { Evaluation, Participant } from '@/types/evaluation.types';
import { ParticipantType, InvitationStatus, EvaluationStatus } from '@/types/evaluation.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar as CalendarIcon, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSaveEvaluation } from '@/hooks';

const MIN_NAME_LENGTH = 3;
const MAX_NAME_LENGTH = 128;
const MAX_SUMMARY_LENGTH = 1024;

export function EvaluationNew() {
  const { t } = useTranslation(['wizard', 'common']);
  const navigate = useNavigate();
  const saveEvaluation = useSaveEvaluation();

  // Form state
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [targetDate, setTargetDate] = useState<Date | null>(null);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Validation functions
  const validateName = (value: string): string | null => {
    if (value.length < MIN_NAME_LENGTH) {
      return t('wizard:validation.nameTooShort');
    }
    if (value.length > MAX_NAME_LENGTH) {
      return t('wizard:validation.nameTooLong');
    }
    return null;
  };

  const validateSummary = (value: string): string | null => {
    if (value.length > MAX_SUMMARY_LENGTH) {
      return t('wizard:validation.summaryTooLong');
    }
    return null;
  };

  const validateForm = (): boolean => {
    const nameError = validateName(name);
    const summaryError = validateSummary(summary);

    if (nameError || summaryError) {
      setErrors({
        ...(nameError && { name: nameError }),
        ...(summaryError && { summary: summaryError })
      });
      return false;
    }
    setErrors({});
    return true;
  };

  // Create evaluation
  const handleCreate = () => {
    if (!validateForm()) {
      return;
    }

    // Auto-create Host participant
    const hostParticipant: Participant = {
      id: storageService.generateId(),
      name: 'You',
      email: '',
      participantType: ParticipantType.Host,
      criteriaGroupAssignments: null, // Host can score all groups
      invitedDate: new Date(),
      invitationExpirationDate: null, // Host is automatically accepted, no expiration
      lastActivityDate: null,
      invitationStatus: InvitationStatus.Accepted, // Host is automatically accepted
    };

    const newEvaluation: Evaluation = {
      id: storageService.generateId(),
      name,
      summary: summary.trim() || undefined,
      targetCompletionDate: targetDate,
      targetSolutions: [],
      criteriaGroups: [],
      participants: [hostParticipant],
      invitationExpirationDays: 7, // Default: 7 days until invitation expires
      status: EvaluationStatus.Organizing, // New evaluations start in Organizing status
      statusChangedDate: new Date(),
      closedReason: null,
      createdDate: new Date(),
      modifiedDate: new Date(),
    };

    // Use React Query mutation to save and invalidate cache
    saveEvaluation.mutate(newEvaluation, {
      onSuccess: () => {
        navigate(`/evaluation/${newEvaluation.id}`);
      },
    });
  };

  // Helper for character counters
  const nameLength = name.length;
  const summaryLength = summary.length;
  const isNameValid = nameLength >= MIN_NAME_LENGTH && nameLength <= MAX_NAME_LENGTH;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t('common:navigation.backToHome')}
      </Button>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight mb-2">{t('wizard:title')}</h1>
        <p className="text-muted-foreground">
          {t('wizard:basicInfo.description')}
        </p>
      </div>

      {/* Single Form */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('wizard:basicInfo.title')}</CardTitle>
            <CardDescription>
              {t('wizard:basicInfo.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Evaluation Name */}
            <div className="space-y-2">
              <Label htmlFor="eval-name">
                {t('wizard:basicInfo.nameLabel')} <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="eval-name"
                  placeholder={t('wizard:basicInfo.namePlaceholder')}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) {
                      const newErrors = { ...errors };
                      delete newErrors.name;
                      setErrors(newErrors);
                    }
                  }}
                  onBlur={() => {
                    const error = validateName(name);
                    if (error) {
                      setErrors({ ...errors, name: error });
                    }
                  }}
                  className={cn(errors.name && 'border-destructive')}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'eval-name-error' : undefined}
                  autoFocus
                />
                {isNameValid && (
                  <>
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-600" aria-hidden="true" />
                    <span className="sr-only">Valid name entered</span>
                  </>
                )}
              </div>
              <div className="flex justify-between items-center text-xs">
                {errors.name ? (
                  <span id="eval-name-error" className="text-destructive" role="alert">{errors.name}</span>
                ) : (
                  <span className="text-muted-foreground">
                    {t('wizard:validation.nameTooShort')}
                  </span>
                )}
                <span className={cn(
                  'text-muted-foreground',
                  nameLength > MAX_NAME_LENGTH && 'text-destructive'
                )}>
                  {nameLength}/{MAX_NAME_LENGTH}
                </span>
              </div>
            </div>

            {/* Target Completion Date */}
            <div className="space-y-2">
              <Label>{t('wizard:basicInfo.targetDateLabel')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !targetDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {targetDate ? (
                      <span>{t('common:date.targetDate')}: {format(targetDate, 'PPP')}</span>
                    ) : (
                      <span>{t('common:date.notSet')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={targetDate || undefined}
                    onSelect={(date) => setTargetDate(date || null)}
                    initialFocus
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    defaultMonth={new Date()}
                    numberOfMonths={2}
                  />
                  {targetDate && (
                    <div className="p-3 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        onClick={() => setTargetDate(null)}
                      >
                        <X className="mr-2 h-4 w-4" />
                        {t('common:actions.clear')}
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
              {targetDate && (
                <Badge variant="secondary" className="mt-2">
                  <CalendarIcon className="mr-1 h-3 w-3" />
                  {format(targetDate, 'MMMM d, yyyy')}
                </Badge>
              )}
            </div>

            {/* Evaluation Summary */}
            <div className="space-y-2">
              <Label htmlFor="eval-summary">
                {t('wizard:basicInfo.summaryLabel')}
              </Label>
              <Textarea
                id="eval-summary"
                placeholder={t('wizard:basicInfo.summaryPlaceholder')}
                value={summary}
                onChange={(e) => {
                  setSummary(e.target.value);
                  if (errors.summary) {
                    const newErrors = { ...errors };
                    delete newErrors.summary;
                    setErrors(newErrors);
                  }
                }}
                onBlur={() => {
                  const error = validateSummary(summary);
                  if (error) {
                    setErrors({ ...errors, summary: error });
                  }
                }}
                className={cn(
                  'min-h-[120px] resize-none',
                  errors.summary && 'border-destructive'
                )}
                aria-invalid={!!errors.summary}
                aria-describedby={errors.summary ? 'eval-summary-error' : 'eval-summary-help'}
                maxLength={MAX_SUMMARY_LENGTH}
              />
              <div className="flex justify-between items-center text-xs">
                {errors.summary ? (
                  <span id="eval-summary-error" className="text-destructive" role="alert">{errors.summary}</span>
                ) : (
                  <span id="eval-summary-help" className="text-muted-foreground">
                    {t('wizard:basicInfo.summaryHelp')}
                  </span>
                )}
                <span className={cn(
                  'text-muted-foreground',
                  summaryLength > MAX_SUMMARY_LENGTH && 'text-destructive'
                )}>
                  {summaryLength}/{MAX_SUMMARY_LENGTH}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/')}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!isNameValid || saveEvaluation.isPending}
            size="lg"
          >
            <Check className="mr-2 h-5 w-5" />
            {saveEvaluation.isPending ? 'Creating...' : t('wizard:actions.createEvaluation')}
          </Button>
        </div>
      </div>
    </div>
  );
}
